import * as Sentry from '@sentry/react';

export type RpcErrorObject = {
  code: number;
  message: string;
  data?: unknown;
};

type RpcSuccessResponse<R> = {
  jsonrpc: '2.0';
  result: R;
  id: number;
};

type RpcErrorResponse = {
  jsonrpc: '2.0';
  error: RpcErrorObject;
  id: number | null;
};

type RpcResponse<R> = RpcSuccessResponse<R> | RpcErrorResponse;

export class RpcError extends Error {
  readonly code: number;
  readonly data?: unknown;

  constructor(code: number, message: string, data?: unknown) {
    super(message);
    this.name = 'RpcError';
    this.code = code;
    this.data = data;
  }
}

type BackendApiErrorContext = {
  endpoint: string;
  httpStatus?: number;
  transport: 'http' | 'jsonrpc';
};

const BACKEND_API_ERROR_CAPTURED_SYMBOL = Symbol(
  'backendApiErrorCapturedInSentry',
);

type CapturableBackendApiError = Error & {
  [BACKEND_API_ERROR_CAPTURED_SYMBOL]?: boolean;
};

export function captureBackendApiError(
  error: Error,
  context: BackendApiErrorContext,
): void {
  const capturableError = error as CapturableBackendApiError;

  if (capturableError[BACKEND_API_ERROR_CAPTURED_SYMBOL]) {
    return;
  }

  capturableError[BACKEND_API_ERROR_CAPTURED_SYMBOL] = true;

  Sentry.withScope(scope => {
    scope.setTag('errorSource', 'backend');
    scope.setTag('apiEndpoint', context.endpoint);
    scope.setTag('apiTransport', context.transport);

    if (typeof context.httpStatus === 'number') {
      scope.setTag('httpStatus', String(context.httpStatus));
    }

    if (error instanceof RpcError) {
      scope.setTag('rpcCode', String(error.code));
    }

    Sentry.captureException(error);
  });
}

const INVALID_AUTH_TOKEN_ERROR_CODE = -32000;
const INVALID_AUTH_TOKEN_ERROR_MESSAGE = 'try err: record not found';

export function isInvalidAuthTokenError(error: unknown): boolean {
  return (
    error instanceof RpcError &&
    error.code === INVALID_AUTH_TOKEN_ERROR_CODE &&
    error.message === INVALID_AUTH_TOKEN_ERROR_MESSAGE
  );
}

let rpcToken: string | null = null;

function shouldUseDevProxy(value: string | undefined): boolean {
  return value?.trim() === 'true';
}

function normalizeApiBaseUrl(value: string | undefined): string | null {
  if (!value) {
    return null;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return null;
  }

  return normalizedValue.endsWith('/')
    ? normalizedValue.slice(0, -1)
    : normalizedValue;
}

export function setRpcToken(token: string | null): void {
  rpcToken = token;
}

export function getRpcAuthToken(): string | null {
  return rpcToken;
}

export function getApiBaseUrl(): string {
  if (shouldUseDevProxy(import.meta.env.VITE_USE_DEV_PROXY)) {
    return '';
  }

  const apiBaseUrl = normalizeApiBaseUrl(import.meta.env.VITE_API_URL);

  if (apiBaseUrl) {
    return apiBaseUrl;
  }

  throw new Error('VITE_API_URL is required unless VITE_USE_DEV_PROXY=true');
}

let requestId = 0;

function nextId(): number {
  requestId += 1;
  return requestId;
}

export async function rpcCall<R>(
  method: string,
  params: Record<string, unknown> = {},
): Promise<R> {
  const id = nextId();
  const token = rpcToken;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const requestBody = JSON.stringify({
    jsonrpc: '2.0',
    method,
    params: [params],
    id,
  });

  const rpcUrl = `${getApiBaseUrl()}/rpc`;

  let response: Response;
  try {
    response = await fetch(rpcUrl, {
      method: 'POST',
      headers,
      body: requestBody,
    });
  } catch (cause) {
    throw new RpcError(-32000, 'Network request failed', cause);
  }

  let payload: RpcResponse<R>;
  try {
    payload = (await response.json()) as RpcResponse<R>;
  } catch {
    if (!response.ok) {
      const error = new RpcError(-32000, `HTTP ${response.status}`);
      captureBackendApiError(error, {
        endpoint: method,
        httpStatus: response.status,
        transport: 'jsonrpc',
      });
      throw error;
    }

    const error = new RpcError(-32700, 'Malformed JSON in response');
    captureBackendApiError(error, {
      endpoint: method,
      httpStatus: response.status,
      transport: 'jsonrpc',
    });
    throw error;
  }

  if ('error' in payload && payload.error != null) {
    const error = new RpcError(
      payload.error.code,
      payload.error.message,
      payload.error.data,
    );
    captureBackendApiError(error, {
      endpoint: method,
      httpStatus: response.status,
      transport: 'jsonrpc',
    });
    throw error;
  }

  return (payload as RpcSuccessResponse<R>).result;
}

export type PingResult = { pong: string };

export async function pingHealth(): Promise<PingResult> {
  return rpcCall<PingResult>('Health.Ping');
}
